import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {findPrimaryTicketId} from '../../../../../../libs/inboxes/business/src/ticketing/conversation';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useTimeout} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {ConversationTopbarPill} from '../../../../../../libs/shared/ui/src/components/pill/conversationTopbarPill';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useClipboard} from '../../../../../../libs/shared/ui/src/hooks/clipboardHookHelpers';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ConversationModel} from '../../../../../models/api/conversationModel';

/*
 * Style.
 */

const StyledDivider = styled.div`
  border-left: 1px solid ${(p) => p.theme.greys.shade40};
  height: 16px;
  margin-right: 12px;
`;

const StyledDiv = styled.div`
  display: flex;
  margin-left: 1px;
  align-items: center;
`;

const HiddenText = styled.span`
  visibility: hidden;
`;

const IconContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HidableIcon = styled(Icon)`
  visibility: hidden;
`;

const StyledText = styled.span`
  visibility: inherit;
`;

const StyledConversationTopbarPill = styled(ConversationTopbarPill)<{$wasJustCopied: boolean}>`
  background-color: ${(p) => (p.$wasJustCopied ? p.theme.greys.shade40 : p.theme.greys.shade10)};

  &:hover ${StyledText} {
    visibility: hidden;
  }

  &:hover ${HidableIcon} {
    visibility: inherit;
  }
`;

/*
 * Constants.
 */

const animationDuration = 2000; // ms

/*
 * Props.
 */

interface ConversationTopbarTicketIdProps {
  conversation: ConversationModel;
}

/*
 * Component.
 */

export const ConversationTopbarTicket: FC<ConversationTopbarTicketIdProps> = (props) => {
  const theme = useUserTheme();
  const [wasJustCopied, setWasJustCopied] = useState(false);
  const {copy} = useClipboard();
  const {conversation} = props;
  const [safeSetTimeout] = useTimeout();

  const ticketId = findPrimaryTicketId(conversation);

  useEffect(() => {
    setWasJustCopied(false); // Reset when the conversation changes
  }, [conversation.id]);

  if (!ticketId) {
    return null;
  }
  const onCopyClick = () => {
    copy(ticketId);
    setWasJustCopied(true);

    safeSetTimeout(() => {
      setWasJustCopied(false);
    }, animationDuration);
  };

  const renderPillContent = () => {
    if (wasJustCopied) {
      return (
        <>
          <HiddenText>{ticketId}</HiddenText>
          <IconContainer>
            <Icon name="checkmarkBoxTiny" color={theme.palette.green.shade40} />
          </IconContainer>
        </>
      );
    }

    return (
      <>
        <IconContainer>
          <HidableIcon name="copyTiny" />
        </IconContainer>
        <StyledText>{ticketId}</StyledText>
      </>
    );
  };

  return (
    <StyledDiv>
      <StyledDivider />
      <TooltipCoordinator
        render={() => !wasJustCopied && <Trans id="ticket-id-item-copy">Ticket ID (click to copy)</Trans>}
        placement="bottom"
        hideOnMouseDown
      >
        <ChromelessButton onClick={onCopyClick}>
          <StyledConversationTopbarPill $wasJustCopied={wasJustCopied}>
            {renderPillContent()}
          </StyledConversationTopbarPill>
        </ChromelessButton>
      </TooltipCoordinator>
    </StyledDiv>
  );
};
